import { FC, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import AvailabilityModal from "components/Client/AvailabilityModal";
import {
  Eye,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  CheckCircle,
  Clock,
  FileText,
  XCircle,
  Loader,
  Building2,
  Users,
  Euro,
  MoreVertical,
} from "lucide-react";
import { Location } from "data/types";
import Pagination from "shared/Pagination/Pagination";

export interface ModernApprovedSpacesGridProps {
  locations: Location[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onDeleteSpace: (locationId: string) => void;
}

const ModernApprovedSpacesGrid: FC<ModernApprovedSpacesGridProps> = ({
  locations,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  onDeleteSpace,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [availabilityModal, setAvailabilityModal] = useState<{
    isOpen: boolean;
    locationId: string;
    locationTitle: string;
  }>({
    isOpen: false,
    locationId: "",
    locationTitle: "",
  });

  const handleAvailabilityClick = (locationId: string, locationTitle: string) => {
    setAvailabilityModal({
      isOpen: true,
      locationId,
      locationTitle,
    });
  };

  const getStatusBadge = (validation: string) => {
    const statusConfig = {
      approuvé: {
        icon: <CheckCircle className="w-4 h-4" />,
        label: "Publié",
        className:
          "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800",
      },
      "en attente": {
        icon: <Clock className="w-4 h-4" />,
        label: "En attente",
        className:
          "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800",
      },
      brouillon: {
        icon: <FileText className="w-4 h-4" />,
        label: "Brouillon",
        className:
          "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800",
      },
      "en cours": {
        icon: <Loader className="w-4 h-4" />,
        label: "En cours",
        className:
          "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800",
      },
      bloqué: {
        icon: <XCircle className="w-4 h-4" />,
        label: "Bloqué",
        className:
          "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
      },
    };

    const config = statusConfig[validation as keyof typeof statusConfig] || statusConfig.brouillon;

    return (
      <div
        className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.className}`}
      >
        {config.icon}
        <span>{config.label}</span>
      </div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="h-48 bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div className="p-6 space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
              <div className="flex space-x-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20 animate-pulse"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16 animate-pulse"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-20"
      >
        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
          <Building2 className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Aucun espace trouvé</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Commencez par créer votre premier espace événementiel
        </p>
        <Link
          to="/add-location"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Building2 className="w-5 h-5" />
          <span>Créer mon premier espace</span>
        </Link>
      </motion.div>
    );
  }

  return (
    <div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8"
      >
        <AnimatePresence>
          {locations.map((location) => (
            <motion.div
              key={location.id}
              variants={cardVariants}
              whileHover={{ y: -5 }}
              className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={
                    location?.images && location.images.length > 0
                      ? `${process.env.REACT_APP_CLOUD_FRONT}${location.images[0]}`
                      : "/images/placeholder-space.jpg"
                  }
                  alt={location.title || "Espace"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Status Badge */}
                <div className="absolute top-3 right-3">{getStatusBadge(location.validation || "brouillon")}</div>

                {/* Quick Actions Overlay */}
                <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex space-x-2">
                    {location.validation === "approuvé" && (
                      <Link
                        to={`/location/${location.id}`}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-sm"
                      >
                        <Eye className="w-4 h-4 text-gray-700" />
                      </Link>
                    )}
                    {location.validation === "approuvé" && (
                      <button
                        onClick={() =>
                          handleAvailabilityClick(location.id?.toString() || "", location.title || "Sans titre")
                        }
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-sm"
                      >
                        <Calendar className="w-4 h-4 text-gray-700" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors line-clamp-1">
                    {location.title || "Espace sans nom"}
                  </h3>
                  <div className="relative">
                    <button
                      onClick={() =>
                        setSelectedLocation(
                          selectedLocation === location.id?.toString() ? null : location.id?.toString()
                        )
                      }
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {selectedLocation === location.id?.toString() && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10"
                        >
                          <div className="py-2">
                            <Link
                              to={`/update-location/${location.id}`}
                              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              <span>Modifier</span>
                            </Link>
                            {location.validation === "approuvé" && (
                              <Link
                                to={`/location/${location.id}`}
                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                <span>Voir les détails</span>
                              </Link>
                            )}
                            {location.validation === "approuvé" && (
                              <button
                                onClick={() =>
                                  handleAvailabilityClick(
                                    location.id?.toString() || "",
                                    location.title || "Sans titre"
                                  )
                                }
                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full text-left"
                              >
                                <Calendar className="w-4 h-4" />
                                <span>Gérer les disponibilités</span>
                              </button>
                            )}
                            <hr className="my-2 border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => onDeleteSpace(location.id?.toString() || "")}
                              className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Supprimer</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span className="line-clamp-1">{location.address || "Adresse non spécifiée"}</span>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{location.visitor || "N/A"}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                      <Euro className="w-4 h-4" />
                      <span>{location.price ? `${location.price}€` : "Prix à définir"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center"
        >
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        </motion.div>
      )}

      {/* Availability Modal */}
      <AvailabilityModal
        isOpen={availabilityModal.isOpen}
        onClose={() => setAvailabilityModal((prev) => ({ ...prev, isOpen: false }))}
        locationId={availabilityModal.locationId}
        locationTitle={availabilityModal.locationTitle}
      />
    </div>
  );
};

export default ModernApprovedSpacesGrid;
